import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, LinearProgress, Chip, Stack } from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  School as SchoolIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Button from './UIKit/Button';

const getDifficultyColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'beginner': case 'начинающий': return 'success';
    case 'intermediate': case 'средний': return 'warning';
    case 'advanced': case 'продвинутый': return 'error';
    default: return 'default';
  }
};

const formatTime = (minutes, t) => {
  if (!minutes || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours} ${t('common:time.shortHour')} ${mins} ${t('common:time.shortMin')}`;
  if (hours > 0) return `${hours} ${t('common:time.shortHour')}`;
  return `${mins} ${t('common:time.shortMin')}`;
};

const ActiveLearningPathCard = ({ learningPath, progress = null, nextCourse = null }) => {
  const { t } = useTranslation(['common', 'learningPaths', 'profile']);
  const navigate = useNavigate();

  if (!learningPath?._id) return null;

  const { _id, title, description, image, difficulty, courses, estimatedTimeMinutes } = learningPath;

  const handleContinue = (e) => {
    e.stopPropagation();
    if (nextCourse) {
      navigate(`/courses/${nextCourse.slug || nextCourse._id}`);
    } else {
      navigate(`/learning-paths/${_id}`);
    }
  };
  const imageUrl = image || '/images/default-path.jpg';
  const coursesCount = courses?.length || 0;
  const timeLabel = formatTime(estimatedTimeMinutes, t);

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        minHeight: '260px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        '&:hover .card-overlay': { opacity: 1 },
        mb: 2,
      }}
      onClick={() => navigate(`/learning-paths/${_id}`)}
    >
      {/* Background image */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
        }}
      />

      {/* Gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.15) 100%)',
          zIndex: 1,
        }}
      />

      {/* Difficulty chip — top right */}
      {difficulty && (
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
          <Chip
            label={difficulty}
            color={getDifficultyColor(difficulty)}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2, p: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h5"
          sx={{
            color: '#fff',
            fontWeight: 700,
            mb: 0.5,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.75)',
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Typography>
        )}

        {/* Stats row */}
        <Stack direction="row" spacing={2} mb={2} alignItems="center">
          {coursesCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SchoolIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {coursesCount} {t('common:stats.courses')}
              </Typography>
            </Box>
          )}
          {timeLabel && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {timeLabel}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Progress bar */}
        {progress !== null && (
          <Box mb={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {t('common:stats.progress')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.25)',
                '& .MuiLinearProgress-bar': { borderRadius: 3 },
              }}
            />
          </Box>
        )}

        {/* CTA button */}
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={handleContinue}
        >
          {t('profile:continue_learning')}
        </Button>
      </Box>
    </Box>
  );
};

export default ActiveLearningPathCard;
